import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { MiniGameDef } from '@/core/content/schemas/minigames';
import type { Reward } from '@/core/content/schemas/progression';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import type { HandlerEnv } from './placement/handlers';
import { grantRewards } from './rewards';

const RECENT_RUNS = 20;
/** Hand hints a mini game may report as seen (Bếp của bà §4.4); anything else is ignored. */
const KNOWN_ACTIONS = new Set(['wash', 'tap', 'drag', 'stir', 'hold', 'slice', 'spread', 'roll', 'plate']);

/** Level a mini game entry offers: the first one not cleared yet (each needs the previous), else level 1. */
export function nextMiniGameLevel(levelsCleared: readonly number[]): number {
  return [1, 2, 3].find((l) => !levelsCleared.includes(l)) ?? 1;
}

/** Repeat reward: 15 at the pass line, rising linearly to 25 at `repeatScoreFull` (minigames.md §1.4). */
export function repeatCoins(def: MiniGameDef, score: number): number {
  const floor = def.levels[0]?.passScore ?? 0.7;
  const span = Math.max(1e-6, def.repeatScoreFull - floor);
  const t = Math.min(1, Math.max(0, (score - floor) / span));
  return def.repeatReward.min + Math.round(t * (def.repeatReward.max - def.repeatReward.min));
}

/** Play-clock ms until the next repeat reward (0 = ready). Cooldown counts only while the game is visible. */
export function miniGameCooldownMs(def: MiniGameDef, state: GameSave): number {
  const last = state.miniGames[def.id]?.lastRewardAtPlayMs ?? null;
  if (last === null) return 0;
  return Math.max(0, last + def.repeatCooldownSec * 1000 - state.clock.playTimeMs);
}

/** Play-clock ms until the next `repeatBonus` (0 = ready, or the game has none). */
export function miniGameBonusCooldownMs(def: MiniGameDef, state: GameSave): number {
  const last = state.miniGames[def.id]?.lastBonusAtPlayMs ?? null;
  if (last === null || def.repeatBonusCooldownSec === undefined) return 0;
  return Math.max(0, last + def.repeatBonusCooldownSec * 1000 - state.clock.playTimeMs);
}

/**
 * finishMiniGame (minigames.md §1.2 step 6). First clear pays the level's rewards and ignores the cooldown;
 * later completions pay 15–25 coins when the cooldown has elapsed, plus the level's `repeatBonus` (a homemade
 * dish, §4.11) when its own longer cooldown has elapsed too. Idempotent by runId.
 */
export function finishMiniGame(
  draft: GameSave,
  cmd: CommandOf<'finishMiniGame'>,
  env: HandlerEnv,
): CommandResult {
  const { result } = cmd;
  const def = env.content.miniGames.get(result.miniGameId);
  const levelDef = def?.levels.find((l) => l.level === result.level);
  if (!def || !levelDef) return { ok: false, error: 'NOT_FOUND' };
  const state = (draft.miniGames[def.id] ??= {
    levelsCleared: [],
    firstClearAtPlayMs: {},
    bestScore: {},
    plays: 0,
    lastRewardAtPlayMs: null,
    recentRunIds: [],
    lastBonusAtPlayMs: null,
    seenNoteKeys: [],
    seenActions: [],
  });
  if (state.recentRunIds.includes(result.runId)) return { ok: true, events: [], replayed: true };
  state.recentRunIds = [...state.recentRunIds, result.runId].slice(-RECENT_RUNS);
  state.plays += 1;
  const newActions = (result.seenActions ?? []).filter(
    (a) => KNOWN_ACTIONS.has(a) && !state.seenActions.includes(a),
  );
  if (newActions.length > 0) state.seenActions = [...state.seenActions, ...new Set(newActions)];
  if (
    result.completed &&
    result.noteKey !== undefined &&
    levelDef.noteKeys.includes(result.noteKey) &&
    !state.seenNoteKeys.includes(result.noteKey)
  )
    state.seenNoteKeys = [...state.seenNoteKeys, result.noteKey];

  const now = draft.clock.playTimeMs;
  const score = Math.min(1, Math.max(0, result.score));
  const events: DomainEvent[] = [];
  let rewards: Reward[] = [];
  let firstClear = false;
  const passed = result.completed && score >= levelDef.passScore;

  if (result.completed) {
    const levelKey = String(result.level);
    state.bestScore[levelKey] = Math.max(state.bestScore[levelKey] ?? 0, score);
    if (passed && !state.levelsCleared.includes(result.level)) {
      firstClear = true;
      rewards = [...levelDef.firstClear];
      state.levelsCleared = [...state.levelsCleared, result.level].sort((a, b) => a - b);
      state.firstClearAtPlayMs[levelKey] = now;
      state.lastRewardAtPlayMs = now;
      // The first clear already hands over dishes: the next homemade bonus waits its full cooldown.
      if (levelDef.repeatBonus) state.lastBonusAtPlayMs = now;
      events.push(...grantRewards(draft, env.content, `mg:${def.id}:L${levelKey}`, rewards, 'reward'));
    } else if (miniGameCooldownMs(def, draft) === 0) {
      rewards = [{ type: 'coins', amount: repeatCoins(def, score) }];
      if (levelDef.repeatBonus && passed && miniGameBonusCooldownMs(def, draft) === 0) {
        rewards.push(...levelDef.repeatBonus);
        state.lastBonusAtPlayMs = now;
      }
      state.lastRewardAtPlayMs = now;
      events.push(...grantRewards(draft, env.content, `mg:${result.runId}`, rewards, 'reward'));
    }
  }

  events.unshift({
    type: 'MINIGAME_COMPLETED',
    miniGameId: def.id,
    runId: result.runId,
    level: result.level,
    completed: result.completed,
    score,
    passed,
    firstClear,
    rewards,
    cooldownRemainingMs: miniGameCooldownMs(def, draft),
  });
  return { ok: true, events };
}
